/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { CDN_URLS, OCI_FOLDERS } from '../config';

/**
 * Constructs a full image URL from a path, prepending the appropriate CDN base URL if necessary.
 * @param path The path or full URL of the image.
 * @param cdn The CDN to use (defaults to GCS).
 * @returns The full URL.
 */
export const getImageUrl = (path: string, cdn: string = CDN_URLS.GCS): string => {
    if (!path) return '';
    if (path.startsWith('http') || path.startsWith('data:') || path.startsWith('blob:')) {
        return path;
    }
    // Ensure we don't double slash
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    const cleanCdn = cdn.endsWith('/') ? cdn : `${cdn}/`;

    return `${cleanCdn}${cleanPath}`;
};

/**
 * Resolves an image URL using a multi-CDN strategy for high availability.
 * @param path The image path fragment.
 * @param group The CDN group to use (from MULTI_CDN_CONFIG).
 * @returns A full URL from one of the CDNs in the group.
 */
export const getMultiCdnUrl = (path: string, _group: string = 'UI'): string => {
    if (!path) return '';
    if (path.startsWith('http') || path.startsWith('data:') || path.startsWith('blob:')) {
        return path;
    }

    // Check if the path belongs to a known OCI folder
    const ociPrefixes = Object.values(OCI_FOLDERS);
    if (ociPrefixes.some(prefix => path.startsWith(prefix))) {
        return getImageUrl(path, CDN_URLS.OCI_STORE);
    }

    // Default to GCS for other content
    return getImageUrl(path, CDN_URLS.GCS);
};

/**
 * Specifically for avatar images, typically hosted on GitHub or similar.
 */
export const getAvatarUrl = (path: string): string => {
    return getImageUrl(path, CDN_URLS.GITHUB);
};

// --- Google Photos Picker API ---

/**
 * Represents a session created for picking photos.
 */
export interface PickerSession {
    id: string;
    pickerUri: string;
    isCompleted: boolean;
}

/**
 * Represents a media item returned by the Picker API.
 */
export interface PickerMediaItem {
    mediaItem: {
        id: string;
        baseUrl: string;
        filename: string;
        mimeType: string;
    };
}

/**
 * Creates a new Picker session to allow the user to select photos.
 * @param accessToken The OAuth 2.0 access token.
 * @returns The created session.
 */
export const createPickerSession = async (accessToken: string): Promise<PickerSession> => {
    const response = await fetch('https://photopicker.googleapis.com/v1/sessions', {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(`Failed to create Picker session: ${error.error?.message || response.statusText}`);
    }

    return response.json();
};

/**
 * Fetches the media items selected by the user in a completed session.
 * @param accessToken The OAuth 2.0 access token.
 * @param sessionId The ID of the completed session.
 * @returns A list of selected media items.
 */
export const fetchPickerMediaItems = async (accessToken: string, sessionId: string): Promise<PickerMediaItem[]> => {
    const response = await fetch(`https://photopicker.googleapis.com/v1/mediaItems?sessionId=${sessionId}`, {
        headers: {
            'Authorization': `Bearer ${accessToken}`
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(`Failed to fetch media items: ${error.error?.message || response.statusText}`);
    }

    const data = await response.json();
    return data.mediaItems || [];
};
